import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FlagIcon, GraduationCapIcon, HeartHandshakeIcon, UsersIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
interface CommitmentCard {
  title: string;
  body: string;
  icon: React.ReactNode;
}
const commitments: CommitmentCard[] = [{
  title: 'Veterans & Military',
  body: 'We actively recruit and hire military veterans, recognizing the valuable skills and mission focus they bring to our team. A significant portion of our workforce are veterans, and we provide transition support for service members entering the private sector.',
  icon: <FlagIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-1103" data-mp="1nirp50hdipay" />
}, {
  title: 'STEM Education',
  body: 'We partner with local schools and organizations to promote STEM education and careers in technology. Our team members volunteer as mentors and speakers, and we sponsor coding bootcamps and scholarship programs.',
  icon: <GraduationCapIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-1104" data-mp="14fo4w348jpi4" />
}, {
  title: 'Community Giving',
  body: 'New River Systems supports charitable organizations through annual contributions and employee volunteer programs. We provide paid volunteer time off and match employee donations to qualified nonprofits.',
  icon: <HeartHandshakeIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-1105" data-mp="1n41lj2h0mfs4" />
}, {
  title: 'Diversity & Inclusion',
  body: "We're committed to building a diverse and inclusive workplace. We actively recruit from underrepresented communities and maintain partnerships with organizations that promote diversity in technology.",
  icon: <UsersIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-1106" data-mp="1icb3sbfr0pqo" />
}];
interface SupportedOrganization {
  name: string;
  alt: string;
  url: string;
  logo: string;
}
const organizations: SupportedOrganization[] = [{
  name: 'Special Operations Warrior Foundation',
  alt: 'Special Operations Warrior Foundation',
  url: 'https://specialops.org/',
  logo: 'https://jobs.newriversystems.com/wp-content/uploads/2024/05/client-logos-SpecialOpsWarrior-Foundation-400.png'
}, {
  name: 'Navy SEAL Foundation',
  alt: 'Navy Seal Foundation',
  url: 'https://www.navysealfoundation.org/',
  logo: 'https://jobs.newriversystems.com/wp-content/uploads/2024/05/client-logos-NavySeal-Foundation-400.png'
}, {
  name: 'Fisher House',
  alt: 'Fisher House',
  url: 'https://www.fisherhouse.org/',
  logo: 'https://jobs.newriversystems.com/wp-content/uploads/2024/05/client-logos-FisherHouse-400.png'
}, {
  name: 'Storybook Treasures',
  alt: 'Storybook Treasures',
  url: 'https://www.storybooktreasures.org/',
  logo: 'https://jobs.newriversystems.com/wp-content/uploads/2024/05/client-logos-Storybook-treasures-400.png'
}];
export function CommunityPage() {
  return <div className="w-full" data-id="element-1107" data-mp="1iz7j70szbky2">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern" data-id="element-1108" data-mp="1e0ntcpb9lp0f">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1109" data-mp="1e3a1aq22i6j6">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-1110" data-mp="3pqdj5zmpgx9x">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-1111" data-mp="3ulst4ghle912">
              Community Impact
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1112" data-mp="7inzhvbfl5hs"></div>
            <p className="text-xl text-nrs-light font-bold max-w-2xl mx-auto" data-id="element-1113" data-mp="7ga7jvc1s9eg">
              Giving back to the communities we serve
            </p>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY COMMITMENT */}
      <section className="section-white" data-id="element-1114" data-mp="3fy7rkr0d8hv9">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1115" data-mp="3fx53equfgtj1">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1116" data-mp="3kq7v0tfn9swl">
            Community Commitment
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1117" data-mp="2n997vpglh7nu"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1118" data-mp="2s7txyw24uayk">
            {commitments.map(item => <div key={item.title} className="flex flex-col h-full bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-1119" data-mp="yqxzdh6gpm0b">
                <div className="flex h-11 w-11 items-center justify-center rounded-md border border-gray-200 text-nrs-navy mb-5" data-id="element-1120" data-mp="toyvm66t8n2u">
                  {item.icon}
                </div>
                <h3 className="font-heading font-bold text-lg text-nrs-navy mb-3 leading-snug" data-id="element-1121" data-mp="12q6uoqh5hrxz">
                  {item.title}
                </h3>
                <p className="text-nrs-gray text-sm leading-relaxed" data-id="element-1122" data-mp="1chtaz18cih7l">
                  {item.body}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ORGANIZATIONS WE SUPPORT */}
      <section className="section-light-blue" data-id="element-1123" data-mp="1mfh8fyxc62tg">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1124" data-mp="1mdmj88f17ojk">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1125" data-mp="3s97gd4mo1uil">
            Organizations We Support
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1126" data-mp="3o8wyatllndnd"></div>
          <p className="text-center text-lg text-nrs-gray mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-1127" data-mp="3e4gn4dj2ekpw">
            We're proud to support organizations making a difference for service
            members, military families, education, and our communities.
          </p>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center" data-id="element-1128" data-mp="39bxabis7btm5">
            {organizations.map(org => <li key={org.name} className="w-full flex justify-center" data-id="element-1129" data-mp="2ueemqjr9fmga">
                <a href={org.url} target="_blank" rel="noopener noreferrer" className="group flex h-full w-full max-w-[226px] items-center justify-center rounded-lg bg-white border border-gray-200 p-5 shadow-sm transition-colors duration-200 ease-out hover:border-nrs-orange/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nrs-orange focus-visible:ring-offset-2" data-id="element-1130" data-mp="2zej2g7kcgd5g">
                  <img src={org.logo} alt={org.alt} loading="lazy" width={226} height={226} className="h-[226px] w-[226px] max-w-full object-contain" data-id="element-1131" data-mp="omrcmk9bqs6f" />
                </a>
              </li>)}
          </ul>
        </div>
      </section>

      {/* COMPANY CREDENTIALS */}
      <section className="section-white" data-id="element-1132" data-mp="i82argyt3ak7">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1133" data-mp="d63520flg1k3">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-8 border border-gray-200 rounded-lg p-8" data-id="element-1134" data-mp="87rwru4y5z3z">
            <a href="https://jobs.newriversystems.com/home/" target="_blank" rel="noopener noreferrer" className="flex-shrink-0 rounded-md transition-opacity duration-200 ease-out hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nrs-orange focus-visible:ring-offset-2" data-id="element-1135" data-mp="15vtlwh8m1hjw">
              <img src="https://newriversystems.com/wp-content/uploads/2024/02/Service-Disabled-Veteran-Owned-Certified.png" alt="Veteran Owned Business" loading="lazy" width={100} height={125} className="h-[125px] w-[100px] object-contain" data-id="element-1136" data-mp="r5mdkucbhewx" />
            </a>
            <div className="text-center sm:text-left" data-id="element-1137" data-mp="1r6hf2jiullh2">
              <h2 className="text-xl font-bold mb-2" data-id="element-1138" data-mp="1r7rcr7lsgrn6">
                Service-Disabled Veteran-Owned Small Business
              </h2>
              <p className="text-nrs-gray leading-relaxed" data-id="element-1139" data-mp="1w7dsbnp3ajz5">
                New River Systems Corporation is a certified Service-Disabled
                Veteran-Owned Small Business, a credential that reflects both
                our founding and the veterans who continue to shape our
                workforce today.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-light-blue" data-id="element-1140" data-mp="2jvey4ouvvmav">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1141" data-mp="2jvnbehux4tml">
          <h3 className="text-2xl font-bold mb-8" data-id="element-1142" data-mp="2ot6q1ivj38f2">
            Want to Learn More About Our Community Impact?
          </h3>
          <Link to="/contact-us" className="btn-primary" data-id="element-1143" data-mp="2tv5qajvz1p7s">
            Contact Us
          </Link>
        </div>
      </section>
    </div>;
}